"use client";

import { Row, Col, Form, Alert, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useSignInForm } from "./../hooks/use-signin-form";
import TwoFactorVerification from "./../TwoFactorVerification";
import ResendConfirmationEmail from "./../ResendConfirmationEmail";
import GoogleSignInButton from "./../GoogleSignInButton";
import { useEffect, useState } from "react";

const   SignInForm = () => {
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    sessionStorage.removeItem("bannerShown");
  }, []);

  const {
    isSubmitting,
    submitError,
    errors,
    register,
    handleSubmit,
    onSubmit,
    loginWithGoogle,
    twoFactorRequired,
    twoFactorUserId,
    userName,
    resetTwoFactor,
    notConfirmed,
  } = useSignInForm();

  return (
    <>
      <div className="auth-main-content m-auto m-1230 px-3">
        <Row className="align-items-center">
          <Col lg={6} className="d-none d-lg-block">
            <img
              src="/images/login_mth.jpg"
              className="rounded-3"
              alt="login"
              width={646}
              height={804}
            />
          </Col>

          <Col lg={6}>
            {twoFactorRequired && twoFactorUserId ? (
              <TwoFactorVerification
                userId={twoFactorUserId}
                userName={userName}
                onBack={resetTwoFactor}
              />
            ) : (
            <div className="mw-480 ms-lg-auto">
              <div className="d-inline-block mb-4">
                <img
                  src="/images/logo.svg"
                  className="rounded-3 for-dark-logo"
                  alt="login"
                  width={200}
                />
              </div>

              <h3 className="fs-28 mb-4">
                ¡Bienvenido de nuevo a MultyHealth!
              </h3>

              {submitError && (
                <Alert variant="danger" className="mb-4">
                  {submitError}
                </Alert>
              )}

              {notConfirmed && (
                <Alert variant="warning" className="mb-4">
                  <div className="fw-semibold mb-1">
                    Tu cuenta aún no ha sido activada
                  </div>

                  <div className="mb-2">
                    Hemos enviado un correo electrónico a{" "}
                    <strong>{notConfirmed.email}</strong>. Por favor, revisa tu
                    bandeja de entrada y sigue las instrucciones para activar tu
                    cuenta.
                  </div>

                  {notConfirmed.emailSent && (
                    <div className="mb-2">
                      Acabamos de reenviarte el correo de confirmación.
                    </div>
                  )}

                  <ResendConfirmationEmail
                    email={notConfirmed.email}
                    initialCooldown={notConfirmed.retryAfterSeconds}
                    initialLimitReached={notConfirmed.limitReached}
                  />
                </Alert>
              )}

              <Form onSubmit={handleSubmit(onSubmit)}>
                <Form.Group className="mb-4">
                  <label className="label text-secondary">
                    Correo Electrónico
                  </label>
                  <Form.Control
                    type="email"
                    className="h-45"
                    placeholder="ejemplo@multisalud.com"
                    {...register("email", {
                      required: "El correo electrónico es obligatorio",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Dirección de correo inválida",
                      },
                    })}
                  />
                  {errors.email && (
                    <small className="text-danger">
                      {errors.email.message}
                    </small>
                  )}
                </Form.Group>

                <Form.Group className="mb-4">
                  <label className="label text-secondary">Contraseña</label>

                  <div style={{ position: "relative" }}>
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      className="h-45"
                      placeholder="Escribe tu contraseña"
                      {...register("password", {
                        required: "La contraseña es obligatoria",
                      })}
                      style={{ paddingRight: "40px" }} // espacio para el botón dentro
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                      }}
                    >
                      {showPassword ? (
                        <span className="material-symbols-outlined">
                          visibility_off
                        </span>
                      ) : (
                        <span className="material-symbols-outlined">
                          visibility
                        </span>
                      )}
                    </button>
                  </div>

                  {errors.password && (
                    <small className="text-danger">
                      {errors.password.message}
                    </small>
                  )}
                </Form.Group>

                <Form.Group className="mb-4">
                  <Link
                    to="/forgot-password"
                    className="fw-medium text-primary text-decoration-none"
                  >
                    ¿Olvidaste tu contraseña?
                  </Link>
                </Form.Group>

                <Form.Group className="mb-4">
                  <button
                    type="submit"
                    className="btn btn-primary fw-medium py-2 px-3 w-100"
                    disabled={isSubmitting}
                  >
                    <div className="d-flex align-items-center justify-content-center py-1">
                      {isSubmitting ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                          />
                          <span className="ms-2">Procesando...</span>
                        </>
                      ) : (
                        <>
                          <span className="material-symbols-outlined fs-20 text-white me-2">
                            login
                          </span>
                          <span>Iniciar Sesión</span>
                        </>
                      )}
                    </div>
                  </button>
                </Form.Group>

                <Form.Group className="mb-4">
                  <div className="d-flex align-items-center gap-2 mb-3 text-muted">
                    <hr className="flex-grow-1 m-0" />
                    <small>o</small>
                    <hr className="flex-grow-1 m-0" />
                  </div>
                  <GoogleSignInButton onCredential={loginWithGoogle} />
                </Form.Group>

                <Form.Group>
                  <p>
                    ¿No tienes una cuenta?{" "}
                    <Link
                      to="/sign-up"
                      className="fw-medium text-primary text-decoration-none"
                    >
                      Regístrate
                    </Link>
                  </p>
                </Form.Group>
              </Form>
            </div>
            )}
          </Col>
        </Row>
      </div>

      {/* Boton temporal para autención y formulario de google para soporte */}
      {/* <FloatingSupportButton url="https://docs.google.com/forms/d/e/1FAIpQLSfO23DecpC74Fah3wNDUSgHsaWjRbRxbKOvCRJgdM-_h5EEKQ/viewform?usp=dialog" /> */}
    </>
  );
};

export default SignInForm;
